import { Home, Car, Briefcase, Filter, Search, MapPin, Heart, Eye, Plus, MessageSquare, FileText, GraduationCap, Users, HelpCircle, Building, Globe, Music, Cpu, TrendingUp, Wrench, Armchair, Shirt, MoreHorizontal, ChevronDown, ChevronUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import useSound from '../hooks/useSound'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { listingService } from '../lib/listingService'
import { zocdocService } from '../lib/zocdocService'
import { messageService } from '../lib/messageService'
import type { Listing, ListingFilters, MarketplaceMainCategory, HealthProviderListing } from '../types/listing'
import { GROUP_CATEGORIES } from '../types/group'
import { useAuth } from '../contexts/AuthContext'
import OptimizedImage from '../components/OptimizedImage'

const ListingsPage = () => {
  const navigate = useNavigate()
  const playClick = useSound('click')
  const playNavigation = useSound('navigation')
  const { user } = useAuth()
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<ListingFilters>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [activeMainCategory, setActiveMainCategory] = useState<MarketplaceMainCategory | 'all'>('all')
  const [featuredListings, setFeaturedListings] = useState<Listing[]>([])
  const [normalListings, setNormalListings] = useState<Listing[]>([])
   const [currentSlide, setCurrentSlide] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [searchParams] = useSearchParams()

  // Group featured listings by category
  const groupedFeatured = featuredListings.reduce((acc, listing) => {
    const category = listing.mainCategory || 'other'
    if (!acc[category]) acc[category] = []
    acc[category].push(listing)
    return acc
  }, {} as Record<string, typeof featuredListings>)

  // Count listings per category
  const categoryCounts = GROUP_CATEGORIES.map(category => {
    const count = listings.filter(listing => listing.mainCategory === category.id).length
    return { ...category, count }
  })

  // Get category name from ID
  const getCategoryName = (categoryId: string) => {
    if (categoryId === 'other') return 'Outros'
    const category = GROUP_CATEGORIES.find(cat => cat.id === categoryId)
    return category ? category.name : categoryId
  }

  // Map URL category parameter to internal mainCategory
  const mapUrlCategoryToMainCategory = (urlCategory: string): MarketplaceMainCategory | undefined => {
    const mapping: Record<string, MarketplaceMainCategory> = {
      'imoveis': 'housing',
      'veiculos': 'cars',
      'servicos': 'services',
      'housing': 'housing',
      'cars': 'cars',
      'services': 'services',
      'saude': 'saude',
      'jobs': 'jobs',
      'documents': 'documents',
      'education': 'education',
      'community': 'community',
      'questions': 'questions',
      'politica': 'politica',
      'imigracao': 'imigracao',
      'cultura': 'cultura',
      'tecnologia': 'tecnologia',
      'negocios': 'negocios',
      'furniture': 'furniture',
      'electronics': 'electronics',
      'fashion': 'fashion',
      'others': 'others',
    }
    return mapping[urlCategory]
  }

  // Initialize filters from URL query parameters
  useEffect(() => {
    const categoryParam = searchParams.get('category')
    const featuredParam = searchParams.get('featured')
    const newFilters: ListingFilters = {}

    if (categoryParam) {
      const mainCategory = mapUrlCategoryToMainCategory(categoryParam)
      if (mainCategory) {
        newFilters.mainCategory = mainCategory
        setActiveMainCategory(mainCategory)
      }
    }

    if (featuredParam === 'true') {
      newFilters.isPaid = true
    }

    // Only update filters if there are changes to avoid infinite loops
    if (Object.keys(newFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...newFilters }))
    }
  }, [searchParams])

  useEffect(() => {
    const loadListings = async () => {
      setLoading(true)

      try {
        // Always load regular listings
        const regularData = await listingService.getListings({ ...filters, approvalStatus: 'approved' })
        let data = [...regularData]
        
        // If mainCategory is 'saude', also load Zocdoc providers
        if (filters.mainCategory === 'saude') {
          try {
            const zocdocFilters = {
              location: filters.location,
              specialty: filters.subCategory,
              // Map other filters as needed
            }
            let providers = await zocdocService.searchProviders(zocdocFilters)
            // Apply additional filters (price, location, etc.)
            providers = filterHealthProviders(providers, filters)
            data = [...regularData, ...providers]
          } catch (zocdocError) {
            console.error('Failed to load Zocdoc providers:', zocdocError)
            // Continue with regular data only
          }
        }
        
        setListings(data)
        
        // Separate featured (paid) and normal listings
        // Zocdoc providers have their own featuredLevel field
        const featured = data.filter(listing => listing.isPaid && listing.featuredLevel !== 'none')
        const normal = data.filter(listing => !listing.isPaid || listing.featuredLevel === 'none')
        setFeaturedListings(featured)
        setNormalListings(normal)
      } catch (error) {
        console.error('Failed to load listings:', error)
      } finally {
        setLoading(false)
      }
    }
    loadListings()
  }, [filters])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % Math.ceil(GROUP_CATEGORIES.length / 3))
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters(prev => ({
      ...prev,
      location: searchQuery || undefined,
    }))
  }

  const handleMainCategoryFilter = (category: MarketplaceMainCategory | 'all') => {
    setActiveMainCategory(category)
    setFilters(prev => ({
      ...prev,
      mainCategory: category === 'all' ? undefined : category,
    }))
  }

  const handleCreateListing = () => {
    playNavigation()
    if (!user) {
      if (window.confirm('Você precisa estar logado para criar um anúncio. Deseja fazer login agora?')) {
        navigate('/login')
      }
      return
    }
    navigate('/listings/new')
  }

  const handleStartChat = async (listing: Listing) => {
    playClick()
    
    // For health providers, redirect to Zocdoc booking
    if (listing.type === 'health') {
      const healthListing = listing as HealthProviderListing
      if (healthListing.providerLocationId) {
        // In a real integration, we would use Zocdoc's booking flow
        // For now, open Zocdoc website with provider info
        window.open(`https://www.zocdoc.com/doctor/${healthListing.providerLocationId}`, '_blank')
      } else {
        window.open('https://www.zocdoc.com', '_blank')
      }
      return
    }
    
    if (!user) {
      if (window.confirm('Você precisa estar logado para enviar uma mensagem. Deseja fazer login agora?')) {
        navigate('/login')
      }
      return
    }
    // Prevent chatting with yourself
    if (user.uid === listing.userId) {
      alert('Você não pode enviar mensagem para seu próprio anúncio.')
      return
    }
    try {
      const conversationId = await messageService.findOrCreateListingConversation(
        user.uid,
        listing.userId,
        listing.id,
        listing.title,
        listing.price,
        listing.images[0]
      )
      navigate(`/messages/${conversationId}`)
    } catch (error) {
      console.error('Failed to start chat:', error)
      alert('Erro ao iniciar conversa. Tente novamente.')
    }
  }

  const formatPrice = (price: number, unit?: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: unit === 'BRL' ? 'BRL' : 'USD',
    }).format(price)
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'property': return <Home className="w-5 h-5" />
      case 'car': return <Car className="w-5 h-5" />
      case 'service': return <Briefcase className="w-5 h-5" />
      case 'health': return <Heart className="w-5 h-5" />
      default: return <Home className="w-5 h-5" />
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'property': return 'Imóvel'
      case 'car': return 'Carro'
      case 'service': return 'Serviço'
      case 'generic': return 'Outros'
      case 'health': return 'Saúde'
      default: return type
    }
  }

  const getContactButtonLabel = (type: string) => {
    switch (type) {
      case 'health': return 'Agendar'
      default: return 'Contato'
    }
  }

  const filterHealthProviders = (providers: HealthProviderListing[], filters: ListingFilters) => {
    let filtered = providers
    if (filters.minPrice !== undefined) {
      filtered = filtered.filter(p => p.price >= filters.minPrice!)
    }
    if (filters.maxPrice !== undefined) {
      filtered = filtered.filter(p => p.price <= filters.maxPrice!)
    }
    if (filters.location) {
      const locationLower = filters.location.toLowerCase()
      filtered = filtered.filter(p => 
        p.location.toLowerCase().includes(locationLower) ||
        p.city.toLowerCase().includes(locationLower) ||
        p.state.toLowerCase().includes(locationLower)
      )
    }
    // Note: other filters like status, approvalStatus, etc. don't apply to health providers
    return filtered
  }

   const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/10 text-emerald-400'
      case 'sold': return 'bg-red-500/10 text-red-400'
      case 'pending': return 'bg-amber-500/10 text-amber-400'
      default: return 'bg-noe-800 text-noe-300'
    }
  }

    const getCategoryIcon = (categoryId: string, className: string = 'w-4 h-4 flex-shrink-0') => {
     switch (categoryId) {
       case 'housing': return <Home className={className} />
       case 'cars': return <Car className={className} />
       case 'jobs': return <Briefcase className={className} />
       case 'documents': return <FileText className={className} />
       case 'education': return <GraduationCap className={className} />
       case 'services': return <Wrench className={className} />
       case 'community': return <Users className={className} />
       case 'questions': return <HelpCircle className={className} />
       case 'politica': return <Building className={className} />
       case 'imigracao': return <Globe className={className} />
       case 'saude': return <Heart className={className} />
       case 'cultura': return <Music className={className} />
       case 'tecnologia': return <Cpu className={className} />
       case 'negocios': return <TrendingUp className={className} />
       case 'furniture': return <Armchair className={className} />
       case 'electronics': return <Cpu className={className} />
       case 'fashion': return <Shirt className={className} />
       case 'others': return <MoreHorizontal className={className} />
       default: return <Home className={className} />
     }
   }

  return (
    <div className="flex-grow w-full max-w-full overflow-x-hidden md:overflow-x-visible space-y-6 md:space-y-8 pb-12 md:pb-20">
      {/* Header */}
       <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 w-[90%] mx-auto max-w-7xl">
        <div>
          <h1 className="text-4xl font-bold font-serif tracking-tight">Marketplace</h1>
          <p className="text-noe-400 mt-2">Encontre imóveis, carros e serviços oferecidos pela comunidade</p>
        </div>
        <div className="flex items-center gap-4">
            <button
             onClick={handleCreateListing}
               className="px-3 py-1.5 sm:px-4 sm:py-2 bg-noe-brand text-white rounded-lg font-medium hover:bg-noe-brand/80 transition-colors flex items-center gap-2"
           >
             <Plus className="w-4 h-4" />
             Criar Anúncio
           </button>
          <div className="text-sm text-noe-500">
            <span className="text-noe-brand font-bold">{listings.length}</span> anúncios ativos
          </div>
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
        </div>
      </div>

       {/* Filters */}
        <div className="bg-noe-900/30 border border-noe-800 rounded-2xl p-6 w-[90%] mx-auto max-w-7xl mb-6 md:mb-8">
          {isMobile && (
            <button
              onClick={() => { playClick(); setFiltersExpanded(!filtersExpanded); }}
               className="flex items-center justify-between w-full mb-4 md:mb-6 px-4 py-3 bg-noe-900/50 border border-noe-800 rounded-lg text-noe-300 hover:bg-noe-800 transition-colors"
            >
              <span className="font-medium">Filtros e busca</span>
              {filtersExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          )}

               <div className={isMobile && !filtersExpanded ? 'hidden' : 'block'}>
                 <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
               {/* Category filters */}
               <div className="relative min-w-0 flex-1 overflow-hidden">

                 {/* Categories list - always visible on desktop, conditionally on mobile */}
                  <div className="flex flex-nowrap gap-1.5 sm:gap-2 overflow-x-auto pb-4 w-full max-w-full min-w-0 scrollbar-thin">
                   <button
                     onClick={() => { playClick(); handleMainCategoryFilter('all'); }}
                     className={`px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors whitespace-nowrap flex-shrink-0 ${activeMainCategory === 'all' ? 'bg-noe-brand text-white' : 'bg-noe-900/50 text-noe-300 hover:bg-noe-800'}`}
                   >
                     Todas
                   </button>
                   {GROUP_CATEGORIES.map((category) => (
                     <button
                       key={category.id}
                       onClick={() => { playClick(); handleMainCategoryFilter(category.id as MarketplaceMainCategory); }}
                       className={`px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${activeMainCategory === category.id ? 'bg-noe-brand text-white' : 'bg-noe-900/50 text-noe-300 hover:bg-noe-800'}`}
                     >
                       {getCategoryIcon(category.id)}
                       <span>{category.name}</span>
                     </button>
                   ))}
                 </div>
               </div>



          {/* Search */}
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
             <input
               type="text"
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por localização..."
                className="pl-12 pr-4 py-3 bg-noe-900/50 border border-noe-800 rounded-lg text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all w-full sm:w-64 md:w-80"
             />
             <button
               type="submit"
               onClick={playClick}
               className="absolute right-3 top-1/2 -translate-y-1/2 text-noe-brand hover:text-noe-brandHover"
             >
              Buscar
            </button>
          </form>
        </div>

        {/* Additional filters */}
        <div className="mt-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-noe-500">
            <Filter className="w-4 h-4" />
            Filtros:
          </div>
          <select
            value={filters.status || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
             className="px-3 py-2 bg-noe-900/50 border border-noe-800 rounded-lg text-noe-100 text-sm focus:outline-none focus:ring-2 focus:ring-noe-brand/50 w-full sm:w-auto"
          >
            <option value="">Todos status</option>
            <option value="active">Ativo</option>
            <option value="sold">Vendido</option>
            <option value="pending">Pendente</option>
          </select>
          <select
            value={filters.transactionType || ''}
            onChange={(e) => {
              const val = e.target.value
              setFilters(prev => ({ ...prev, transactionType: val === 'sale' || val === 'rent' ? val : undefined }))
            }}
             className="px-3 py-2 bg-noe-900/50 border border-noe-800 rounded-lg text-noe-100 text-sm focus:outline-none focus:ring-2 focus:ring-noe-brand/50 w-full sm:w-auto"
          >
            <option value="">Todos os tipos</option>
            <option value="sale">Venda</option>
            <option value="rent">Aluguel</option>
          </select>
          <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto">
            <input
              type="number"
              placeholder="Preço mínimo"
              value={filters.minPrice || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value ? Number(e.target.value) : undefined }))}
              className="px-3 py-2 bg-noe-900/50 border border-noe-800 rounded-lg text-noe-100 text-sm focus:outline-none focus:ring-2 focus:ring-noe-brand/50 w-full sm:w-32"
            />
            <span className="text-noe-500 hidden sm:inline">—</span>
            <input
              type="number"
              placeholder="Preço máximo"
              value={filters.maxPrice || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value ? Number(e.target.value) : undefined }))}
              className="px-3 py-2 bg-noe-900/50 border border-noe-800 rounded-lg text-noe-100 text-sm focus:outline-none focus:ring-2 focus:ring-noe-brand/50 w-full sm:w-32"
            />
          </div>
          <button
             onClick={() => { playClick(); setFilters({}); }}
            className="text-sm text-noe-brand hover:text-noe-brandHover font-medium self-start md:self-auto"
          >
            Limpar filtros
          </button>
        </div>
        </div>
        </div>

        {/* Featured listings slider grouped by category */}
        {Object.keys(groupedFeatured).length > 0 && (
          <div className="mb-10 w-[90%] mx-auto max-w-7xl space-y-10">
            {Object.entries(groupedFeatured).map(([categoryId, listings]) => (
              <div key={categoryId}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Destaques em {getCategoryName(categoryId)}</h2>
                  <div className="flex items-center gap-2 text-sm text-noe-500">
                    <span className="text-noe-brand font-bold">{listings.length}</span> anúncios em destaque
                  </div>
                </div>
                <div className="relative">
                   <div className="flex overflow-x-auto pb-4 gap-4 md:gap-6 scrollbar-thin">
                    {listings.map((listing) => (
                      <div
                        key={listing.id}
                        className="flex-shrink-0 w-80 bg-noe-900/30 border border-noe-800 rounded-2xl overflow-hidden hover:border-noe-brand/30 transition-all group"
                      >
                        {/* Image */}
                        <div className="relative h-48 overflow-hidden bg-noe-800/30">
                          <OptimizedImage
                            src={listing.images[0] || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'}
                            alt={listing.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            width={320}
                            height={192}
                            fallbackSrc="https://via.placeholder.com/320x192/bfa757/ffffff?text=Noé+Connect"
                          />
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                              Destaque
                            </span>
                          </div>
                        </div>

                        {/* Badges section below image - like groups */}
                        <div className="px-6 pt-4 pb-3 border-b border-noe-800">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(listing.status)}`}>
                      {listing.status === 'active' ? 'Ativo' : listing.status === 'sold' ? 'Vendido' : 'Pendente'}
                    </span>
                    <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-noe-800 text-noe-100 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1">
                      {getIcon(listing.type)}
                      {getTypeLabel(listing.type)}
                    </span>
                    {listing.type === 'health' && (
                      <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs sm:text-sm font-medium">
                        Parceria Zocdoc
                      </span>
                    )}
                  </div>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <h3 className="font-bold text-lg line-clamp-1 overflow-hidden text-ellipsis">{listing.title}</h3>
                              <div className="flex items-center gap-2 mt-2 text-sm text-noe-500 min-w-0">
                                <MapPin className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate flex-1 min-w-0">{listing.location}</span>
        </div>

                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-noe-brand truncate">{formatPrice(listing.price, listing.priceUnit)}</div>
                            </div>
                          </div>
                          <p className="mt-4 text-noe-400 line-clamp-2">{listing.description}</p>
                          <div className="mt-6 pt-6 border-t border-noe-800 flex items-center gap-3">
                            <Link
                              to={`/listings/${listing.id}`}
                              onClick={playNavigation}
                              className="flex-1 py-3 bg-noe-brand text-white font-medium rounded-lg hover:bg-noe-brandHover transition-colors text-center"
                            >
                              Ver detalhes
                            </Link>
                            <button
                              onClick={() => handleStartChat(listing)}
                              className="flex-1 py-3 bg-noe-brand text-white font-medium rounded-lg hover:bg-noe-brandHover transition-colors text-center flex items-center gap-2 justify-center"
                            >
                              <MessageSquare className="w-4 h-4" />
                              {getContactButtonLabel(listing.type)}
                            </button>
                           </div>
         </div>
       </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

       {/* Listings grid */}
       {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-noe-900/30 border border-noe-800 rounded-2xl h-96 animate-pulse"></div>
            ))}
          </div>
       ) : normalListings.length === 0 ? (
         <div className="text-center py-20">
           <div className="text-noe-500 text-6xl mb-4">🏠</div>
           <h3 className="text-xl font-bold text-noe-300">Nenhum anúncio encontrado</h3>
           <p className="text-noe-500 mt-2">Tente ajustar os filtros ou voltar mais tarde</p>
         </div>
       ) : (
           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-[90%] mx-auto max-w-7xl">
            {normalListings.map((listing) => (
             <div
               key={listing.id}
               className="bg-noe-900/30 border border-noe-800 rounded-2xl overflow-hidden hover:border-noe-brand/30 transition-all group w-full mx-auto"
             >

               {/* Image */}
                <div className="relative h-48 overflow-hidden bg-noe-800/30">
                    <OptimizedImage
                      src={listing.images[0] || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'}
                      alt={listing.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      width={320}
                      height={192}
                      fallbackSrc="https://via.placeholder.com/320x192/bfa757/ffffff?text=Noé+Connect"
                    />

               </div>

               {/* Badges section below image - like groups */}
               <div className="px-6 pt-4 pb-3 border-b border-noe-800">
                 <div className="flex flex-wrap items-center justify-between gap-3">
                   <div className="flex flex-wrap items-center gap-2">
                     <span className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(listing.status)}`}>
                       {listing.status === 'active' ? 'Ativo' : listing.status === 'sold' ? 'Vendido' : 'Pendente'}
                     </span>
                     <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-noe-800 text-noe-100 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1">
                       {getIcon(listing.type)}
                       {getTypeLabel(listing.type)}
                     </span>
                   </div>
                 </div>
               </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between">
                   <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-lg line-clamp-1 overflow-hidden text-ellipsis">{listing.title}</h3>
                      <div className="flex items-center gap-2 mt-2 text-sm text-noe-500 min-w-0">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate flex-1 min-w-0">{listing.location}</span>
                      </div>
                   </div>
                  <div className="text-right">
                     <div className="text-2xl font-bold text-noe-brand truncate">{formatPrice(listing.price, listing.priceUnit)}</div>
                     {listing.type === 'property' && (
                       <div className="text-sm text-noe-500">{listing.bedrooms} quartos • {listing.bathrooms} banheiros</div>
                     )}
                     {listing.type === 'car' && (
                       <div className="text-sm text-noe-500">{listing.year} • {listing.mileage.toLocaleString()} km</div>
                     )}
                     {listing.type === 'service' && (
                       <div className="text-sm text-noe-500">{listing.duration}</div>
                     )}
                     {listing.type === 'health' && (
                        <div className="text-sm text-noe-500">{(listing as HealthProviderListing).specialty}</div>
                     )}
                  </div>
                </div>

                 <p className="mt-4 text-noe-400 line-clamp-2">{listing.description}</p>

                 {/* Additional health provider info */}
                 {listing.type === 'health' && (
                    <div className="mt-4 space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                          ⭐ {(listing as HealthProviderListing).rating || 'N/A'} ({(listing as HealthProviderListing).reviewCount || 0} reviews)
                        </span>
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                          {(listing as HealthProviderListing).acceptsNewPatients ? 'Aceita novos pacientes' : 'Não aceita novos pacientes'}
                        </span>
                      </div>
                      <div className="text-sm text-noe-500">
                        <span className="font-medium">Seguros: </span>
                        {(listing as HealthProviderListing).insuranceAccepted?.slice(0, 3).join(', ')}
                        {(listing as HealthProviderListing).insuranceAccepted?.length > 3 && '...'}
                      </div>
                      <div className="text-sm text-noe-500">
                        <span className="font-medium">Idiomas: </span>
                        {(listing as HealthProviderListing).languages?.join(', ')}
                      </div>
                    </div>
                 )}

                 <div className="mt-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <button
                       onClick={() => navigate(`/profile/${listing.userId}`)}
                       className="p-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-noe-brand rounded-full"
                     >
                       <OptimizedImage
                         src={listing.userAvatar || `https://ui-avatars.com/api/?name=${listing.userName}`}
                         alt={listing.userName}
                         className="w-8 h-8 rounded-full"
                         width={32}
                         height={32}
                         fallbackSrc={`https://ui-avatars.com/api/?name=${listing.userName}`}
                       />
                     </button>
                    <div>
                      <p className="text-sm font-medium">{listing.userName}</p>
                      <p className="text-xs text-noe-500">{new Date(listing.createdAt).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-noe-500">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {listing.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      {listing.likes}
                    </span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-noe-800 flex items-center gap-3">
                    <Link
                      to={`/listings/${listing.id}`}
                      onClick={playNavigation}
                       className="flex-1 py-3 bg-noe-brand text-white font-medium rounded-lg hover:bg-noe-brandHover transition-colors text-center"
                   >
                     Ver detalhes
                   </Link>
                      <button
                        onClick={() => handleStartChat(listing)}
                         className="py-3 px-6 bg-noe-brand text-white font-medium rounded-lg hover:bg-noe-brandHover transition-colors text-center flex items-center gap-2 justify-center"
                     >
                       <MessageSquare className="w-4 h-4" />
                       {getContactButtonLabel(listing.type)}
                     </button>
                 </div>
              </div>
            </div>
          ))}
        </div>
      )}

       {/* Stats */}
        <div className="bg-noe-900/30 border border-noe-800 rounded-2xl p-6 backdrop-blur-sm w-[90%] mx-auto max-w-7xl">
          <div className="relative overflow-hidden">
            <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
              {Array.from({ length: Math.ceil(categoryCounts.length / 3) }).map((_, slideIndex) => (
                 <div key={slideIndex} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 min-w-full">
                  {categoryCounts.slice(slideIndex * 3, slideIndex * 3 + 3).map((category) => (
                    <div key={category.id} className="text-center">
                      <div className="text-4xl font-bold text-noe-brand">{category.count}</div>
                      <div className="text-noe-500 mt-2">{category.name}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {/* Dots indicator */}
            <div className="flex justify-center mt-6 gap-2">
              {Array.from({ length: Math.ceil(categoryCounts.length / 3) }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${idx === currentSlide ? 'bg-noe-brand' : 'bg-noe-800'}`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
    </div>
  )
}

export default ListingsPage